<template>
  <section class="upload">
    <!-- 图片列表 -->
    <div
      v-if="fileUrls && fileUrls.length"
      class="upload-image-list"
      ref="gallery"
      v-viewer="{title: false}"
    >
      <image-item
        v-for="(image, index) in fileUrls"
        :key="index"
        :image="image"
      >
        <div class="image-item-actions">
          <span
            v-if="viewer"
            class="el-upload-list__item-preview"
            @click="handlePictureCardPreview(image, index, 'gallery')"
          >
            <i class="el-icon-zoom-in"></i>
          </span>
          <span
            v-if="!disabled"
            class="el-upload-list__item-delete"
            @click="handleRemove(image, index)"
          >
            <i class="el-icon-delete"></i>
          </span>
        </div>
        <template #progress>
          <el-progress
            v-if="image.progress && image.progress !== 100"
            :percentage="image.progress"
          />
        </template>
      </image-item>
      <div v-if="uploadTip" class="upload-tip">{{uploadTip}}</div>
    </div>
    <data-empty-simple
      v-if="(!$attrs['file-list'] || $attrs['file-list'].length === 0)
        && disabled"
    >
    </data-empty-simple>

    <!-- 点击上传触发区域 -->
    <div v-if="!disabled" class="uploader">
      <el-upload
        v-bind="$attrs"
        v-on="$listeners"
        :auto-upload="true"
        :multiple="multiple"
        name="file"
        :class="{
          'exced': isExced,
        }"
        list-type="picture-card"
        :show-file-list="false"
        :action="action"
        :on-success="handleSuccess"
        :on-exceed="handleExceed"
        :on-progress="handleProgress"
        :on-error="handleError"
        :before-upload="handleBeforeUpload"
        :data="uploadParams"
        :headers="headersParams"
        ref="uploadFile"
      >
        <i slot="default" class="el-icon-plus" />
        <div slot="tip" class="el-upload__tip" v-if="uploadTip">{{uploadTip}}</div>
      </el-upload>
    </div>

    <!-- 粘贴或者拖放上传 -->
    <uploader-by-paste
      v-if="showPaste && !disabled"
      :path="path"
      v-bind="$attrs"
      v-on="$listeners"
    />

    <!-- PDF查看 -->
    <preview-pdf
      :visible.sync="pdfVisible"
      :pdf-url="pdfUrl"
    />
  </section>
</template>

<script>
import ImageItem from './image-item';
import UploaderByPaste from './uploader-by-paste';
import { UPLOAD_FILE_API } from '@/api/global';
import { getAccount } from '@/utils/account';
import { getToken } from '@/utils/auth';
import previewPdf from '@/components/jr-preview/preview-pdf';

export default {
  name: 'Upload',
  components: {
    ImageItem,
    UploaderByPaste,
    previewPdf,
  },
  mixins: [],
  props: {
    name: {
      type: String,
      default: '',
    },
    fileList: {
      type: Array,
      default: () => ([]),
    },
    // 图片上传最多限制数量
    fileLimit: {
      type: Number,
      default: 9999,
    },
    // 是否可以查看大图
    viewer: {
      type: Boolean,
      default: true,
    },
    // 是否显示粘贴上传
    paste: {
      type: Boolean,
      default: true,
    },
    // 禁用编辑
    disabled: {
      type: Boolean,
      default: false,
    },
    // 图片上传大小的最大值(单位MB)
    uploadSize: {
      type: Number,
      default: 5,
    },
    // 上传文件类型
    acceptFileType: {
      type: String,
      default: '.jpg,.jpeg,.png,.bmp,.pdf',
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    // 上传文案提示
    uploadTip: {
      type: String,
      default: '',
    },
    path: {
      type: String,
      default: 'finance-service/',
    },
    // 上传执行状态
    uploading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      pdfVisible: false,
      pdfUrl: '',
      action: UPLOAD_FILE_API,
      progressFlag: false, // 显示进度条
      loadProgress: 0, // 进度条值
      fileLength: 0, // 上传数量
      currentUrl: [], // 当前上传文件数量
      headersParams: {
        Authorization: getToken(),
      },
    };
  },
  inheritAttrs: false,
  computed: {
    fileUrls() {
      return this.fileList;
    },
    isExced() {
      const isExced = this.fileList.length >= this.fileLimit;
      return isExced;
    },
    showPaste() {
      if (!this.paste) {
        return false;
      }
      return !this.isExced;
    },
    uploadParams() {
      return {
        path: this.path,
        operatorName: getAccount().userName,
      };
    },
  },
  methods: {
    handleExceed(files, fileList) {
      const maxLeng = this.$attrs.limit - fileList.length;
      this.$message.warning(`最多还能上传${maxLeng}张图片`);
    },
    handleBeforeUpload(file) {
      const acceptTypeArr = this.acceptFileType.replace(' ', '').toLowerCase().split(','); // 文件拓展名组成的数组
      const fileType = file.name.substring(file.name.lastIndexOf('.'), file.name.length + 1); // 文件拓展名
      const isAcceptType = acceptTypeArr.includes(fileType); // 文件拓展名是否在拓展名数组里
      const isLt5M = file.size / 1024 / 1024 < this.uploadSize;
      if (!isAcceptType) {
        this.$message.error('文件类型不符合要求！');
      } else if (!isLt5M) {
        this.$message.error(`上传图片大小不能超过 ${this.uploadSize}MB!`);
      } else if (this.isExced) {
        this.$message.error(`最多上传${this.fileLimit}个`);
      } else {
        const tempObj = {
          uid: file.uid, // uid用于辨别文件
          name: file.name, // 列表显示的文件名
          progress: 0, // 进度条
          code: 200, // 上传状态
          url: '',
        };
        this.fileUrls.push(tempObj);
      }
      this.$emit('update:uploading', true);
      return isAcceptType && isLt5M;
    },
    handleSuccess(response, file) {
      const { success, data } = response || {};
      this.fileUrls.forEach((element, index) => {
        if (element.uid === file.uid) {
          if (success) {
            element.progress = 100;
            // 将file 文件的url(blob类型的url) 转成 接口放回的 url
            file.url = data.path || '';
            element.url = data.path || '';
            element.fileId = data.fileId || '';
            element.objectName = data.objectName || '';
            this.$message.closeAll();
            this.$message.success('上传成功');
            this.$set(this.fileUrls, index, element);
            this.$emit('update', this.fileUrls, this.name);
          } else {
            this.$message.error('上传失败');
            console.log('上传失败 =>>', response);
          }
        }
      });
      this.$emit('update:uploading', false);
    },
    handleProgress(e, file) {
      this.fileUrls.forEach((element, index) => {
        if (element.uid === file.uid) {
          // 更新这个uid下的进度
          const progress = Math.floor(e.percent);
          // 防止上传完接口还没有返回成功值，所以此处给定progress的最大值为99，成功的钩子中再置为100
          element.progress = progress === 100 ? 99 : progress;
          this.$set(this.fileUrls, index, element);
        }
      });
      // console.log('handleProgress =>>', e);
    },
    handleError(err, file) {
      const { uid } = file;
      const idx = this.fileUrls.findIndex((item) => item.uid === uid);
      this.$message.error('文件上传失败, 请重新上传');
      this.fileUrls.splice(idx, 1);
      this.$emit('update', this.fileUrls);
      this.$emit('update:uploading', false);
    },
    handleRemove(file, index) {
      const { fileUrls } = this;
      const [deleteItem] = fileUrls.splice(index, 1);
      this.$emit('delete', deleteItem);
      this.$emit('update', fileUrls);
    },
    handlePictureCardPreview(file, index, gallery) {
      const isPdf = file.url.includes('.pdf');
      if (isPdf) {
        // window.open(file.url);
        this.pdfVisible = true;
        this.pdfUrl = file.url.replace(/^(http:)/, 'https:');
      } else {
        const images = [];
        let imageIndex = index;
        this.fileUrls.forEach((it) => {
          if (!it.url.includes('.pdf')) {
            images.push(it);
          }
        });
        imageIndex = images.findIndex((el) => el.url === file.url);
        const viewer = this.$refs[gallery].$viewer;
        viewer.view(imageIndex);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.el-progress__text {
  font-size: 12px;
}
.exced {
  display: none;
}
.upload-image-list {
  display: inline-block;
}
.uploader {
  display: inline-block;
  vertical-align: top;
  margin: 0 8px 8px 0;
  &.exceed {
    .el-upload--picture-card {
      display: none;
    }
  }
}

.upload-tip {
  font-size: 12px;
  color: #4d4f51;
}
</style>
